import React, { memo, useCallback } from 'react';
import { Notification } from 'new/entities/Notification';
import { getDateAgoString } from 'js/lib/DateUtils';
import { Stack } from 'new/elements/Stack';
import styles from 'new/elements/Notifications/Notifications.module.scss';
import { Skeleton, Tooltip, Typography } from '@mui/material';
import ReactHtmlParser from 'react-html-parser';
import { ReactComponent as ErrorIcon } from 'new/components/icons/exclamation.svg';
import { ReactComponent as WarningIcon } from 'new/components/icons/warning.svg';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import { EmptyView } from 'new/elements/EmptyView';
import { ReactComponent as BellIcon } from 'new/components/icons/bell.svg';
import PlayIcon from 'js/components/recipeBuilder/icons/PlayIcon';
import { ThreeDotMenu } from 'new/elements/ThreeDotMenu';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as EyeIcon } from 'new/components/icons/eye.svg';
import { useDispatch } from 'react-redux';
import {
  deleteResourceNotification,
  markNotificationAsRead,
  markResourceNotificationAsRead,
  setContextualSupport,
  setContextualSupportVisibility,
} from 'js/actions';
import { Section } from 'new/elements/ThreeDotMenu/ThreeDotMenu';
import { ReactComponent as SourceIcon } from 'new/components/icons/fi-rr-exit.svg';
import { ReactComponent as NexsetIcon } from 'new/components/icons/nexsetsOutline.svg';
import { ReactComponent as DestinationIcon } from 'new/components/icons/fi-rr-arrow-right-to-bracket.svg';
import { IconButton } from 'new/elements/IconButton';
import cx from 'classnames';
import { isObject } from 'lodash';
import { SUPPORT_KEY_NOTIFICATION } from 'js/constants/supportPageKeys';
import { Pagination } from 'new/elements/Pagination';
import { FlowInsightsNavigationButton } from 'new/elements/FlowInsightsNavigationButton/FlowInsightsNavigationButton';
import { NotificationTriageButton } from 'new/elements/Notifications/NotificationTriageButton';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface NotificationsListViewProps {
  items: Notification[];
  totalCount?: number;
  loading?: boolean;
  showResource?: boolean;
  resourceType?: string;
  resourceId?: string;
  compact?: boolean;
  pageCount?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  hideActions?: boolean;
  className?: string;
}

const LevelIcons = {
  ERROR: {
    icon: <ErrorIcon />,
    color: 'error',
  },
  WARNING: {
    icon: <WarningIcon />,
    color: 'warning',
  },
  INFO: {
    icon: <InfoIcon />,
    color: 'primary',
  },
};

const ResourceIcons = {
  SOURCE: {
    icon: <SourceIcon />,
  },
  DATASET: {
    icon: <NexsetIcon />,
  },
  SINK: {
    icon: <DestinationIcon />,
  },
};

const ItemLoader = () => {
  return (
    <Stack direction="row" gap="32px" className={styles.itemContainer}>
      <Skeleton animation="wave" variant="rounded" width="30px" height="30px" />
      <Stack gap="8px" width="100%">
        <Skeleton animation="wave" variant="rounded" width="80%" />
        <Skeleton animation="wave" variant="rounded" width="100px" />
      </Stack>
    </Stack>
  );
};

const Item = (props: {
  item: Notification;
  showResource: boolean;
  compact: any;
  loading?: boolean;
  hideActions?: boolean;
  resourceId?: string;
  resourceType?: string;
}) => {
  const { item, showResource, compact, loading, hideActions } = props;
  const dispatch = useAppDispatch();
  const { created_at, read_at, level, message, id, resource_id, resource_type } = item;

  if (loading) {
    return <ItemLoader />;
  }

  if (!item || !isObject(item)) {
    return null;
  }

  let createdAt = getDateAgoString(created_at);

  const isNotRead = read_at === null;

  let displayMessage = message
    ? ReactHtmlParser(message, {
        decodeEntities: true,
        transform: function transform(node) {
          if (node.type === 'tag' && node.name === 'html') {
            return null;
          }
        },
      })
    : null;
  const iconLevelData = LevelIcons[level] || LevelIcons.INFO;
  const resourceIcon = ResourceIcons[resource_type]?.icon;

  const onMarkAsRead = () => {
    if (resource_id && resource_type) {
      dispatch(markResourceNotificationAsRead(id, resource_type, resource_id));
    } else {
      dispatch(markNotificationAsRead(id));
    }
  };
  const onDelete = () => {
    if (id) {
      dispatch(deleteResourceNotification(id, resource_type, resource_id));
    }
  };

  const actions = [
    {
      actions: [
        {
          icon: <DeleteIcon />,
          color: 'error',
          title: 'Delete',
          onClick: onDelete,
        },
      ],
    },
  ] as Section[];

  if (isNotRead) {
    actions.unshift({
      actions: [
        {
          icon: <EyeIcon />,
          title: 'Mark as Read',
          onClick: onMarkAsRead,
        },
      ],
    });
  }

  return (
    <Stack direction="row" className={styles.itemContainer} data-level={level?.toLowerCase()}>
      <div className={styles.iconContainer}>
        {showResource ? (
          resourceIcon
        ) : (
          <Typography className={styles.itemIcon} color={iconLevelData.color}>
            {iconLevelData.icon}
          </Typography>
        )}
        {isNotRead ? <div className={styles.notReadIndicator} /> : null}
      </div>
      <Stack className={styles.messageContainer}>
        <Typography className={cx({ [styles.compactMessage]: compact })} variant="body2">
          {displayMessage}
        </Typography>
        <Stack direction="row" alignItems="center" gap="4px">
          {showResource ? (
            <Typography className={styles.itemIconSmall} color={iconLevelData.color}>
              {iconLevelData.icon}
            </Typography>
          ) : null}
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            flexWrap="wrap"
            sx={{ rowGap: '6px' }}
          >
            <Typography display="flex" variant="body3" className={styles.createdAt}>
              {createdAt}
            </Typography>
            <FlowInsightsNavigationButton notification={item} />
            <NotificationTriageButton notification={item} />
          </Stack>
        </Stack>
      </Stack>
      {!hideActions && (
        <>
          {actions.length > 1 && <ThreeDotMenu actions={actions} />}
          {actions.length === 1 && (
            <Tooltip title={actions[0].actions[0].title}>
              <IconButton size="small" onClick={actions[0].actions[0].onClick}>
                {actions[0].actions[0].icon}
              </IconButton>
            </Tooltip>
          )}
          {!actions.length && <Stack minWidth="37px" />}
        </>
      )}
    </Stack>
  );
};

const Items = (props) => {
  const { showResource, compact, resourceType, resourceId, items, hideActions = false } = props;

  const renderItem = useCallback(
    (item) => {
      return (
        <Item
          key={item.id}
          showResource={showResource}
          resourceId={resourceId}
          resourceType={resourceType}
          compact={compact}
          item={item}
          hideActions={hideActions}
        />
      );
    },
    [resourceId, resourceType, showResource, compact],
  );

  return (
    <Stack>
      {items.map((item) => {
        return renderItem(item);
      })}
    </Stack>
  );
};

const Loader = () => {
  return (
    <div>
      {new Array(20).fill(0).map((el, index) => (
        <Item key={index} loading item={0} />
      ))}
    </div>
  );
};

const Empty = () => {
  const dispatch = useDispatch();
  const onClickLearn = () => {
    dispatch(setContextualSupport(SUPPORT_KEY_NOTIFICATION));
    dispatch(setContextualSupportVisibility(true));
  };

  return (
    <Stack
      flex={1}
      minHeight="300px"
      maxWidth="400px"
      alignItems="center"
      alignSelf="center"
      justifyContent="center"
    >
      <EmptyView
        title="Notifications"
        message="Nexla produces several different categories of alerts and notifications: Platform Activity, System Alerts, and Data Alerts."
        icon={<BellIcon />}
        actionTitle="Learn more"
        actionProps={{
          startIcon: <PlayIcon />,
          onClick: onClickLearn,
        }}
      />
    </Stack>
  );
};

const Content = (props) => {
  const { loading, items, search } = props;
  if (loading) return <Loader />;
  if (!loading && (!items || !items.length)) return <Empty />;
  return <Items {...props} />;
};

const NotificationsListViewFunction = (props: NotificationsListViewProps) => {
  const onChangePage = (e, page) => {
    if (typeof props.onPageChange === 'function') {
      props.onPageChange(page);
    }
  };

  return (
    <Stack flex={1} className={props.className}>
      <Content {...props} />
      {!props.pageCount || props.pageCount === 1 ? null : (
        <Stack className={styles.pagination}>
          <Pagination count={props.pageCount} page={props.currentPage} onChange={onChangePage} />
        </Stack>
      )}
    </Stack>
  );
};

export const NotificationsListView = memo<NotificationsListViewProps>(
  NotificationsListViewFunction,
);
